<script lang="ts" setup>
import {
  GpDropdown,
  GpTable,
  GpTableColumn,
  type GpTableDataRow,
  GpTextInput,
} from '@gportal/uikit';
import useGpTableData from '@panel/composables/useGpTableData';
import useProjectViewTitle from '@panel/composables/useProjectViewTitle';
import ProjectSettingsLayout from '@panel/layouts/Layout.Project.Settings.vue';
import { type ServiceError } from 'gpcore-apikit/grpc/types';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import type { ProjectLog } from 'gpcore-grpc/gpcore/api/cloud/v1/project_pb';
import type { BasicUser } from 'gpcore-grpc/gpcore/api/cloud/v1/user_pb';
import useDateFormat from 'gpcore-uikit/composables/useDateFormat';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { onMounted, reactive, ref } from 'vue';

useProjectViewTitle('Audit log');

interface LogsTableRow extends GpTableDataRow {
  log: ProjectLog;
  timestamp: string;
  message: string;
  count: number;
}

const logs = ref(new Array<ProjectLog>());
const pagination = reactive({ currentPage: 1, totalPages: 1 });
const user = ref({ key: '', label: 'All users' });
const userOptions = ref<Array<{ key: string; label: string }>>([{ key: '', label: 'All users' }]);
const search = ref('');

const projectStore = useProjectStore();
const { addNotification } = useNotifications();

const format = useDateFormat();
const { tableData } = useGpTableData(logs, (data) =>
  data.map((log) => ({
    key: log.id,
    log,
    timestamp: format(log.createdAt),
    message: log.message,
    count: log.count,
  })),
);

const reloadLogs = (page: number | Event) => {
  pagination.currentPage = page as number;
  projectStore
    .reloadAuditLog({
      page: (page as number) + 1,
      search: search.value,
      userId: user.value.key,
    })
    .then((response) => {
      pagination.totalPages = response.pagination.total;
      logs.value = response.logs;

      // Add users that are no longer in the project
      const options = [...userOptions.value];
      response.logs.forEach((log) => {
        if (!options.some((option) => option.key === log.user!.id)) {
          options.push({
            key: log.user!.id,
            label: log.user!.email || log.user!.username,
          });
        }
      });

      userOptions.value = options;
    })
    .catch((error: ServiceError) => {
      addNotification({ message: error.message, type: 'danger' });
    });
};

const handleUserSelect = (updatedUser) => {
  if (user.value.key !== updatedUser.key) {
    reloadLogs(0);
  }
  user.value = updatedUser;
};

const searchUser = (searchUser: BasicUser) => {
  user.value.key = searchUser.id;
  reloadLogs(0);
};
const handleInput = (searchTerm: string) => {
  search.value = searchTerm;
  reloadLogs(0);
};

onMounted(() => {
  reloadLogs(0);
});
</script>

<template>
  <ProjectSettingsLayout tab-bar-actions-class="tw-w-full md:tw-w-auto">
    <template #buttons>
      <GpTextInput
        :model-value="search"
        class="tw-flex-1 md:tw-w-48 lg:tw-w-64"
        input-id="project-logs-search"
        label=""
        name="search"
        placeholder="Search"
        @update:model-value="handleInput"
        transparent
      />
      <GpDropdown
        :model-value="user"
        class="tw-flex-1 md:tw-w-48 lg:tw-w-64"
        input-id="project-logs-user"
        name="user"
        :data="userOptions"
        :emit-object="true"
        @update:model-value="handleUserSelect"
      />
    </template>

    <template #content>
      <GpTable
        :data="tableData"
        empty-message="No logs found."
        pagination
        :page="pagination.currentPage"
        :max-pages="pagination.totalPages"
        @update:page="reloadLogs"
      >
        <GpTableColumn field="user" label="User">
          <template #body="{ row }: { row: LogsTableRow }">
            <a href="#" @click="searchUser(row.log.user)">
              {{ row.log.user.fullName || row.log.user.username }}
              <br />
              <small>{{ row.log.user.email }}</small>
            </a>
          </template>
        </GpTableColumn>
        <GpTableColumn field="timestamp" label="Timestamp" />
        <GpTableColumn field="message" label="Log">
          <template #body="{ row }: { row: LogsTableRow }">
            <span v-if="row['count'] > 1" class="tw-text-sm tw-text-gray-500"
              >({{ row['count'] }}x)</span
            >
            {{ row['message'] }}
          </template>
        </GpTableColumn>
      </GpTable>
    </template>
  </ProjectSettingsLayout>
</template>
